import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  PLATFORM_ID,
  inject,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ElementRef
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { register } from 'swiper/element/bundle';

if (typeof window !== 'undefined' && !customElements.get('swiper-container')) {
  register();
}

@Component({
  selector: 'app-residences',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './residences.html',
  styleUrl: './residences.css'
})
export class ResidencesComponent implements AfterViewInit, OnDestroy {
  @ViewChild('swiperEl1')   swiperEl1!: ElementRef;
  @ViewChild('swiperEl2')   swiperEl2!: ElementRef;
  @ViewChild('lgContainer1') lgContainer1!: ElementRef;
  @ViewChild('lgContainer2') lgContainer2!: ElementRef;

  private platformId = inject(PLATFORM_ID);
  private lg1: any = null;
  private lg2: any = null;
  private lg1Promise: Promise<void> | null = null;
  private lg2Promise: Promise<void> | null = null;

  readonly slides1 = [
    { src: '/interior/1bed/one_bedroom_interior_1.webp',  label: 'Dining & Kitchen' },
    { src: '/interior/1bed/new_1bed_living.webp',                  label: 'Living Room' },
    { src: '/interior/1bed/1bedroom.webp', label: 'Bedroom' },
  ];

  readonly slides2 = [
    { src: '/interior/2bed/kitchen_view_1.webp',           label: 'Kitchen & Dining Area' },
     { src: '/interior/2bed/new2bed_living.webp',        label: 'Living' },
     { src: '/interior/2bed/2bed_master.webp',        label: 'Master Bedroom' },
    { src: '/interior/2bed/primary_bedroom_2.webp',        label: 'Secondary Bedroom' },
  ];

  readonly pagination = { clickable: true };
  readonly autoplay = { delay: 3800, disableOnInteraction: false, pauseOnMouseEnter: true };
  readonly breakpoints = { 481: { slidesPerView: 1.12, spaceBetween: 20 } };

  readonly fp1Variants = [
    { id: 'a', label: 'Type A', image: '/floorplans/1bedroom_a.png', size: '60.28 m²' },
    { id: 'b', label: 'Type B', image: '/floorplans/1bedroom_b.png', size: '66.13 m²' },
    { id: 'c', label: 'Type C', image: '/floorplans/1bedroom_c.png', size: '59.16 m²' },
  ];

  readonly fp2Variants = [
    { id: 'a', label: 'Type A', image: '/floorplans/2bedroom_a.png', size: '95.74 m²' },
    { id: 'b', label: 'Type B', image: '/floorplans/2bedroom_b.png', size: '97.43 m²' },
    { id: 'c', label: 'Type C', image: '/floorplans/2bedroom_c.png', size: '99.05 m²' },
  ];

  activeView1: 'renders' | 'floorplan' = 'renders';
  activeView2: 'renders' | 'floorplan' = 'renders';
  activeFp1 = 'a';
  activeFp2 = 'a';

  prevSlide1() { this.swiperEl1.nativeElement.swiper.slidePrev(); }
  nextSlide1() { this.swiperEl1.nativeElement.swiper.slideNext(); }
  prevSlide2() { this.swiperEl2.nativeElement.swiper.slidePrev(); }
  nextSlide2() { this.swiperEl2.nativeElement.swiper.slideNext(); }

  async ngAfterViewInit() {
    if (!isPlatformBrowser(this.platformId)) return;
    await Promise.all([this.ensureLg1(), this.ensureLg2()]);
  }

  async openAt1(index: number, event: Event) {
    event.stopPropagation();
    await this.ensureLg1();
    this.lg1?.openGallery(index);
  }

  async openAt2(index: number, event: Event) {
    event.stopPropagation();
    await this.ensureLg2();
    this.lg2?.openGallery(index);
  }

  ngOnDestroy() {
    this.lg1?.destroy();
    this.lg2?.destroy();
  }

  private ensureLg1(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || this.lg1) return Promise.resolve();
    if (!this.lg1Promise) {
      this.lg1Promise = Promise.all([
        import('lightgallery'),
        import('lightgallery/plugins/zoom'),
        import('lightgallery/plugins/fullscreen')
      ]).then(([{ default: lightGallery }, { default: lgZoom }, { default: lgFullscreen }]) => {
        this.lg1 = lightGallery(this.lgContainer1.nativeElement, {
          plugins: [lgZoom, lgFullscreen],
          dynamic: true,
          dynamicEl: this.slides1.map(s => ({ src: s.src, subHtml: `<p>${s.label}</p>` })),
          speed: 500, zoom: true, download: false, counter: true,
        });
      });
    }
    return this.lg1Promise;
  }

  private ensureLg2(): Promise<void> {
    if (!isPlatformBrowser(this.platformId) || this.lg2) return Promise.resolve();
    if (!this.lg2Promise) {
      this.lg2Promise = Promise.all([
        import('lightgallery'),
        import('lightgallery/plugins/zoom'),
        import('lightgallery/plugins/fullscreen')
      ]).then(([{ default: lightGallery }, { default: lgZoom }, { default: lgFullscreen }]) => {
        this.lg2 = lightGallery(this.lgContainer2.nativeElement, {
          plugins: [lgZoom, lgFullscreen],
          dynamic: true,
          dynamicEl: this.slides2.map(s => ({ src: s.src, subHtml: `<p>${s.label}</p>` })),
          speed: 500, zoom: true, download: false, counter: true,
        });
      });
    }
    return this.lg2Promise;
  }
}
